import { useCallback, useState } from 'react';

/**
 * State backed by localStorage. Used for the render/tweak parameters and player
 * preferences, all of which are per-visitor display choices with nothing to sync.
 *
 * Reads and writes are guarded because localStorage throws in private-mode Safari and
 * is absent under SSR; a failure there should never break rendering.
 */
export function useStoredState<T>(
  key: string,
  fallback: T,
  revive: (value: unknown) => T = (value) => value as T,
): [T, (next: T) => void] {
  const [value, setValue] = useState<T>(() => {
    if (typeof window === 'undefined') return fallback;
    try {
      const stored = window.localStorage.getItem(key);
      return stored === null ? fallback : revive(JSON.parse(stored));
    } catch {
      return fallback;
    }
  });

  const update = useCallback(
    (next: T) => {
      setValue(next);
      try {
        window.localStorage.setItem(key, JSON.stringify(next));
      } catch {
        /* storage unavailable — the setting just will not persist */
      }
    },
    [key],
  );

  return [value, update];
}
